<template>
  <div class="tg-page-root min-h-screen bg-white dark:bg-gray-950">
    <!-- Header Section：標題靠左、元素垂直對齊 -->
    <div
      class="mobile-frame-fixed-top fixed top-0 z-30 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-100 dark:border-gray-800 shadow-sm"
    >
      <div class="flex items-center gap-3 px-4 py-4">
        <button
          type="button"
          aria-label="back"
          @click="goBack"
          class="flex items-center justify-center w-10 h-10 rounded-xl text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-800 active:scale-95 transition-all flex-shrink-0"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 class="text-lg font-bold text-gray-800 dark:text-gray-100 flex-1 text-left truncate">{{ $t('PageTitle_Address_List') }}</h1>
        <div class="w-10 flex-shrink-0"></div>
      </div>
    </div>
    <div class="fixed-header-spacer" style="--fixed-header-spacer-height: 72px; height: 72px;"></div>

    <!-- Address List -->
    <div class="px-4 py-6 pb-28">
      <div
        v-for="address in addressList"
        :key="address.id"
        class="address-card bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 mb-4"
        :class="{ 'is-selected': address.id === selectedAddressId }"
        @click="selectAddress(address)"
      >
        <div class="flex items-center gap-3 mb-3">
          <div class="text-base font-semibold text-gray-800 dark:text-gray-100">{{ address.name }}</div>
          <div class="text-sm text-gray-500 dark:text-gray-300">{{ address.phone }}</div>
          <span
            v-if="address.isDefault"
            class="text-xs text-red-500 bg-red-50 px-2 py-0.5 rounded-md"
          >
            默认地址
          </span>
        </div>

        <div class="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
          {{ address.detail }}
        </div>

        <div class="flex items-center justify-between text-sm">
          <button
            class="flex items-center gap-2 text-gray-600 dark:text-gray-300"
            type="button"
            @click.stop="setDefault(address.id)"
          >
            <span
              class="address-checkbox"
              :class="{ 'is-checked': address.isDefault }"
            >
              <svg v-if="address.isDefault" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </span>
            <span>{{ address.isDefault ? '已默认' : '设为默认地址' }}</span>
          </button>
          <div class="flex items-center gap-4">
            <button class="text-red-500" type="button" @click.stop="noop">删除</button>
            <button class="text-brand" type="button" @click.stop="noop">修改</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Address Button -->
    <div class="tg-bottom-bar fixed bottom-0 left-0 right-0 p-4 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-t border-gray-100 dark:border-gray-800 shadow-lg">
      <button
        class="w-full bg-brand text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-brand/90 active:scale-[0.98] transition-all duration-300"
        type="button"
        @click="goToAddAddress"
      >
        新增收货地址
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import logger from '@/utils/logger'

const router = useRouter()

const addressListKey = 'addressList'
const selectedAddressKey = 'selectedAddress'

const mockAddressList = [
  {
    id: 1,
    name: '张德帅',
    phone: '+86 13387162111',
    detail: '港湾道1号香港会议展览中心, 湾仔区, 香港岛123号',
    isDefault: true
  },
  {
    id: 2,
    name: '张三网',
    phone: '+86 12323213222',
    detail: '潇洒三点定位去外地前五大, 3吃大餐123号',
    isDefault: false
  }
]

const addressList = ref([])
const selectedAddressId = ref(null)

const goBack = () => {
  router.back()
}

const goToAddAddress = () => {
  router.push({ path: '/address/add' })
}

const setDefault = (id) => {
  addressList.value = addressList.value.map((item) => ({
    ...item,
    isDefault: item.id === id
  }))
  saveAddressList()
  const selected = addressList.value.find((item) => item.id === id)
  if (selected) {
    localStorage.setItem(selectedAddressKey, JSON.stringify(selected))
    selectedAddressId.value = id
  }
}

onMounted(() => {
  loadAddressList()
  loadSelectedAddress()
})

const loadAddressList = () => {
  // const stored = localStorage.getItem(addressListKey)
  // if (stored) {
  //   try {
  //     addressList.value = JSON.parse(stored)
  //     return
  //   } catch (error) {
  //     console.warn('Failed to parse address list:', error)
  //   }
  // }
  addressList.value = mockAddressList
  saveAddressList()
}

const saveAddressList = () => {
  localStorage.setItem(addressListKey, JSON.stringify(addressList.value))
}

const loadSelectedAddress = () => {
  const stored = localStorage.getItem(selectedAddressKey)
  if (!stored) {
    return
  }
  try {
    const selected = JSON.parse(stored)
    selectedAddressId.value = selected?.id ?? null
  } catch (error) {
    logger.warn('Failed to parse selected address:', error)
  }
}

const selectAddress = (address) => {
  selectedAddressId.value = address.id
  localStorage.setItem(selectedAddressKey, JSON.stringify(address))
  router.back()
}

const noop = () => {}
</script>

<style scoped>
.address-checkbox {
  width: 18px;
  height: 18px;
  border: 1.5px solid #9ca3af;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: transparent;
}

.address-checkbox.is-checked {
  border-color: #6b7280;
  color: #6b7280;
}

.address-card.is-selected {
  border-color: #7fa14a;
  box-shadow: 0 0 0 1px rgba(127, 161, 74, 0.35);
}
</style>
